import React from 'react';
import FontAwesome from 'react-fontawesome';
import GenericSelect, { Option } from 'react-select';

import DebouncedTextInput from 'components/global/form/DebouncedTextInput';

/* NOTE: Brand on front end = manufacturer on back end */
import { sortByOptions } from 'constants/filter_options';
import { categoryEncode } from 'contexts/link_util';

import { CategoryMapping, GetInstrumentsOptions } from 'types/drupal/InstrumentNode';

import 'awesome-bootstrap-checkbox/awesome-bootstrap-checkbox.css';
import './InventoryFilterInputs.sass';

export interface OwnProps {
    updateQueryString(key: string, value?: string): void;
    updateCategory(value: string): void;
}

export interface DispatchProps {
    toggleFilterView(value: boolean): void;
}

export interface StateProps {
    category_options: CategoryMapping[];
    manufacturers: string[];
    models: string[];
    years: string[]; // TODO: remove all references to years
    isMobile: boolean;
    get_options: Partial<GetInstrumentsOptions>;
    viewFilters: boolean;
    itemsFound: number;
    isSearching: boolean;
}

const pricePoints = [
    '0-500',
    '500-1000',
    '1000-2500',
    '2500-5000',
    '5000-10000'
];

type CombinedProps = StateProps & OwnProps & DispatchProps;

// appeasing the typescript dragons
type StringSelect = new () => GenericSelect<string>;
const Select = GenericSelect as StringSelect;

const getCategorySelectOptions = (category_options: CategoryMapping[]): { label: string; value: string; }[] => {
    return [
        { label: 'Select Category', value: 'select' },
        ...category_options.map(v => ({ label: v.description, value: v.tid.toString() }))
    ];
};

const getSelectOptionsFromArray = (values: string[], select_type: string): { label: string; value: string; }[] => {
    return [
        { label: `Select ${select_type}`, value: 'select' },
        ...values.map(v => ({ label: v, value: v }))
    ];
};

// TODO: a lot of control props are identical, group into default prop objects to reduce boiler plate
class InventoryFilterInputs extends React.Component<CombinedProps> {// = (props) => {
    public handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value) this.props.updateQueryString('year', event.target.value);
        else this.props.updateQueryString('year'); // implicit second param of undefined clears key from query
    }

    public handleCategorySelect = (newValue: Option<string> | Option<string>[]) => {
        const { label, value } = (newValue as Option<string>);
        if (value === 'select') this.props.updateCategory('');
        else this.props.updateCategory(categoryEncode(label));
    }

    public handleFilterSelect = (valueSource: string, newValue: Option<string> | Option<string>[]) => {
        const { value } = (newValue as Option<string>);
        // implicit second param of undefined clears key from query
        if (value === 'select') this.props.updateQueryString(valueSource);
        else this.props.updateQueryString(valueSource, (newValue as Option<string>).value);
    }

    public handleFilterClear = (valueSource: string) => {
        this.props.updateQueryString(valueSource);
    }

    public handleFilterCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (this.props.get_options[event.currentTarget.value] !== undefined) this.props.updateQueryString(event.currentTarget.value); // implicit second param of undefined clears key from query
        else this.props.updateQueryString(event.currentTarget.value, 'true');
    }

    public handleFilterRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        // implicit second param of undefined clears key from query
        if (event.currentTarget.value.length < 1) this.props.updateQueryString('price');
        else this.props.updateQueryString('price', event.currentTarget.value);
    }

    public renderFilterPreview = (style: object = {}) => {
        return (
            <div className="row" style={{ ...{ fontSize: '0.8rem' }, ...style }}>
                <div className="col text-left">
                    <div style={{ padding: '10px 5px' }}>
                        {this.props.isSearching ? ('Searching...') : (<span>{this.props.itemsFound} items found</span>)}
                    </div>
                </div>
                <div className="col text-right">
                    {this.renderClose()}
                </div>
            </div>
        );
    }

    public renderClose = () => {
        return (
            <span className="btn" onClick={() => this.props.toggleFilterView(!this.props.viewFilters)}>
                <small>View Results</small>&nbsp;<FontAwesome name="check-circle" />
            </span>
        );
    }

    public render() {
        const {
            category_options,
            isMobile,
            get_options,
            manufacturers,
            models,
            years
        } = this.props;
        const categoryOptionsForSelect = getCategorySelectOptions(category_options);
        const manufacturerOptionsForSelect = getSelectOptionsFromArray(manufacturers, 'Brand');
        const modelOptionsForSelect = getSelectOptionsFromArray(models, 'Model');
        const yearOptionsForSelect = getSelectOptionsFromArray(years, 'Year');
        return (
            <div className={`Inventory-filter-inputs-container${isMobile ? '-mobile' : ''}`}>
                {this.renderFilterPreview()}
                <div className="Input-grouping">
                    <div>Sort By</div>
                    <Select
                        className="Inventory-filter-sortby-select"
                        name="filter-select-sortby"
                        value={get_options.orderBy ? get_options.orderBy : sortByOptions[0].value}
                        searchable={false}
                        clearable={false}
                        autosize={false}
                        options={sortByOptions}
                        onChange={newOption => this.handleFilterSelect('orderBy', newOption)}
                    />
                </div>
                <div className="Input-grouping Filter-checks">
                    <div className="form-check form-check-inline abc-checkbox">
                        <input
                            className="form-check-input"
                            id="cb-vintage"
                            type="checkbox"
                            value="vintage"
                            // ternary is required as prop values of null/undefined are treated as though the prop is never set
                            checked={get_options.vintage ? true : false}
                            onChange={this.handleFilterCheck}
                        />
                        <label htmlFor="cb-vintage"className="form-check-label">Vintage</label>
                    </div>
                    <div className="form-check form-check-inline abc-checkbox">
                        <input
                            className="form-check-input"
                            id="cb-lefty"
                            type="checkbox"
                            value="lefty"
                            // ternary is required as prop values of null/undefined are treated as though the prop is never set
                            checked={get_options.lefty ? true : false}
                            onChange={this.handleFilterCheck}
                        />
                        <label htmlFor="cb-lefty"className="form-check-label">Lefty</label>
                    </div>
                    <div className="form-check form-check-inline abc-checkbox">
                        <input
                            className="form-check-input"
                            id="cb-as_is"
                            type="checkbox"
                            value="as_is"
                            // ternary is required as prop values of null/undefined are treated as though the prop is never set
                            checked={get_options.as_is ? true : false}
                            onChange={this.handleFilterCheck}
                        />
                        <label htmlFor="cb-as_is"className="form-check-label">AS IS</label>
                    </div>
                    <div className="form-check form-check-inline abc-checkbox">
                        <input
                            className="form-check-input"
                            id="cb-featured"
                            type="checkbox"
                            value="featured"
                            // ternary is required as prop values of null/undefined are treated as though the prop is never set
                            checked={get_options.featured ? true : false}
                            onChange={this.handleFilterCheck}
                        />
                        <label htmlFor="cb-featured"className="form-check-label">Featured</label>
                    </div>
                    <br />
                    <div className="form-check form-check-inline abc-checkbox">
                        <input
                            className="form-check-input"
                            id="cb-memorabilia"
                            type="checkbox"
                            value="memorabilia"
                            // ternary is required as prop values of null/undefined are treated as though the prop is never set
                            checked={get_options.memorabilia ? true : false}
                            onChange={this.handleFilterCheck}
                        />
                        <label htmlFor="cb-memorabilia" className="form-check-label">Memorabilia</label>
                    </div>
                </div>
                {isMobile && (<div className="Input-grouping">
                    <div>
                        Category
                        {get_options.category && <button type="button" className="btn btn-clear" onClick={() => this.handleCategorySelect({ value: 'select' })}>clear</button>}
                    </div>
                    <Select
                        className="Inventory-filter-category-select"
                        name="filter-select-category"
                        value={get_options.category ? get_options.category : categoryOptionsForSelect[0].value}
                        clearable={false}
                        autosize={false}
                        options={categoryOptionsForSelect}
                        onChange={this.handleCategorySelect}
                    />
                </div>)}
                <div className="Input-grouping">
                    <div>
                        Brand
                        {get_options.manufacturer && <button type="button" className="btn btn-clear" onClick={() => this.handleFilterClear('manufacturer')}>clear</button>}
                    </div>{/* NOTE: Brand on front end = manufacturer on back end */}
                    <Select
                        className="Inventory-filter-brand-select"
                        name="filter-select-brand"
                        value={get_options.manufacturer ? get_options.manufacturer : manufacturerOptionsForSelect[0].value}
                        clearable={false}
                        autosize={false}
                        options={manufacturerOptionsForSelect}
                        onChange={newOption => this.handleFilterSelect('manufacturer', newOption)}
                    />
                </div>
                <div className="Input-grouping">
                    <div>
                        Model
                        {get_options.model && <button type="button" className="btn btn-clear" onClick={() => this.handleFilterClear('model')}>clear</button>}
                    </div>
                    <Select
                        className="Inventory-filter-model-select"
                        name="filter-model-category"
                        value={get_options.model ? get_options.model : modelOptionsForSelect[0].value}
                        clearable={false}
                        autosize={false}
                        options={modelOptionsForSelect}
                        onChange={newOption => this.handleFilterSelect('model', newOption)}
                    />
                </div>
                <div className="Input-grouping">
                    <div>Year</div>
                    <div className="input-group">
                        {/* No ternary needed on this one, component takes care of it */}
                        <DebouncedTextInput className="Inventory-filter-year-input" value={get_options.year} onChange={this.handleYearChange} />
                    </div>
                </div>
                <div className="Input-grouping">
                    <div>Prices</div>
                    {/* Left a couple of these out of the map due to differences from the other options */}
                    <div className="form-check abc-radio Price-check">
                        <input className="form-check-input" id="cb-any" type="radio" value="" onChange={this.handleFilterRadio} checked={get_options.price ? false : true} />
                        <label htmlFor="cb-any" className="form-check-label">Any</label>
                    </div>
                    {pricePoints.map((pp: string) => {
                        return(
                            <div key={pp} className="form-check abc-radio Price-check">
                                <input className="form-check-input" id={`cb-${pp}`} type="radio" value={pp} onChange={this.handleFilterRadio} checked={get_options.price === pp ? true : false} />
                                <label htmlFor={`cb-${pp}`} className="form-check-label">{`$${pp.replace('-', ' - $')}`}</label>
                            </div>);
                    })}
                    <div className="form-check abc-radio Price-check">
                        <input className="form-check-input" id="cb-100000" type="radio" value="10000-" onChange={this.handleFilterRadio} checked={get_options.price === '10000-' ? true : false} />
                        <label htmlFor="cb-100000" className="form-check-label">More than $10,000</label>
                    </div>
                </div>
                {this.renderFilterPreview()}
            </div>
        );
    }
}

export default InventoryFilterInputs;



// WEBPACK FOOTER //
// ./src/components/pages/inventory/InventoryFilterInputs.tsx